import { trackDesignSystemAction } from 'analytics';
import forge from 'apis/forge';
import Modal from 'components/Modal';
import RadioCard from 'components/Widget/RadioCard';
import { CATEGORY_LABEL_MAP } from 'designSystem/types';
import { ExportDsAiPreset } from 'dragonmouse-shared/api/export';
import { useEffect, useMemo, useRef, useState } from 'react';
import { shallowEqual, useSelector } from 'react-redux';
import { toast } from 'react-toastify';
import { selectActiveDesignSystem } from 'redux/db/reducers';
import { hideExportDsAiModal } from 'redux/mode/actions';
import { RootReducer } from 'redux/reducers';
import store from 'redux/store';
import { runExportWithToast } from 'components/Widget/ExportReadyToast';
import { buildExportFilename } from 'utils';
import * as Sentry from '@sentry/react';
import * as Shared from '../styles';
import * as S from './styles';

interface Props {
	initialPreset?: ExportDsAiPreset;
	initialComponentNames?: string[];
	initialCustomPrompt?: string;
	onClose: () => void;
}

const PRESET_CARDS: Array<{
	preset: ExportDsAiPreset;
	label: string;
	description: string;
}> = [
	{
		preset: 'all',
		label: 'Complete DS pack',
		description: 'Everything an LLM needs — guidelines, components, tokens, code',
	},
	{
		preset: 'components',
		label: 'Components',
		description: 'Pick components; get summaries, usage, and code',
	},
	{
		preset: 'tokens',
		label: 'Tokens',
		description: 'Colors, type, spacing as descriptive language',
	},
	{
		preset: 'custom',
		label: 'Custom',
		description: 'Write a prompt; an agent assembles a tailored markdown reference',
	},
];

// "ds-branded-header" -> "branded-header"
const tagToDir = (tagName: string): string => tagName.replace(/^ds-/, '');

// Tri-state Select-all control: empty/partial → all, all → none.
const SelectAllRow = ({
	selectedCount,
	totalCount,
	onChange,
}: {
	selectedCount: number;
	totalCount: number;
	onChange: (mode: 'all' | 'clear') => void;
}) => {
	const ref = useRef<HTMLInputElement>(null);
	const allSelected = totalCount > 0 && selectedCount === totalCount;
	const partial = selectedCount > 0 && selectedCount < totalCount;

	useEffect(() => {
		if (ref.current) ref.current.indeterminate = partial;
	}, [partial]);

	return (
		<S.SelectAllControl>
			<input
				ref={ref}
				type="checkbox"
				checked={allSelected}
				onChange={() => onChange(allSelected ? 'clear' : 'all')}
			/>
			Select all
			<S.CountText>
				· {selectedCount} of {totalCount}
			</S.CountText>
		</S.SelectAllControl>
	);
};

// Title-case fallback label for an unknown token domain key
const titleCase = (key: string): string =>
	key
		.replace(/[-_]/g, ' ')
		.replace(/([A-Z])/g, ' $1')
		.trim()
		.split(/\s+/)
		.map((w) => w.charAt(0).toUpperCase() + w.slice(1))
		.join(' ');

const DsAiModal = ({
	initialPreset,
	initialComponentNames,
	initialCustomPrompt,
	onClose,
}: Props) => {
	const { activeDesignSystem, designSystemContent } = useSelector(
		(r: RootReducer) => ({
			activeDesignSystem: selectActiveDesignSystem(r.db, r.mode.activeDesignSystemId),
			designSystemContent: r.mode.designSystemContent,
		}),
		shallowEqual
	);

	const [preset, setPreset] = useState<ExportDsAiPreset>(initialPreset ?? 'all');
	const [customPrompt, setCustomPrompt] = useState(initialCustomPrompt ?? '');
	const [submitted, setSubmitted] = useState(false);

	// All component dir names available in this DS, sorted by display name
	const allComponents = useMemo(() => {
		const comps = designSystemContent?.components?.all ?? [];
		return comps
			.map((c) => ({ dir: tagToDir(c.tagName), label: c.name }))
			.sort((a, b) => a.label.localeCompare(b.label));
	}, [designSystemContent]);

	// All introspected token domains (union of theme.byCategory + styles.tokens.byCategory)
	const allDomains = useMemo(() => {
		const keys = new Set<string>([
			...Object.keys(designSystemContent?.theme?.byCategory ?? {}),
			...Object.keys(designSystemContent?.styles?.tokens?.byCategory ?? {}),
		]);
		return Array.from(keys).sort();
	}, [designSystemContent]);

	const [selectedComponents, setSelectedComponents] = useState<Set<string>>(
		() => new Set(initialComponentNames ?? [])
	);
	const [selectedDomains, setSelectedDomains] = useState<Set<string>>(() => new Set(allDomains));

	//----------------------------------------------------------------------
	// HANDLERS
	//----------------------------------------------------------------------

	const closeModal = () => {
		store.dispatch(hideExportDsAiModal());
		onClose();
	};

	const toggleComponent = (dir: string) => {
		setSelectedComponents((prev) => {
			const next = new Set(prev);
			if (next.has(dir)) next.delete(dir);
			else next.add(dir);
			return next;
		});
	};

	const toggleDomain = (domain: string) => {
		setSelectedDomains((prev) => {
			const next = new Set(prev);
			if (next.has(domain)) next.delete(domain);
			else next.add(domain);
			return next;
		});
	};

	const onGenerate = async () => {
		if (!activeDesignSystem) return;
		if (preset === 'components' && selectedComponents.size === 0) {
			toast.error('Select at least one component');
			return;
		}
		if (preset === 'tokens' && selectedDomains.size === 0) {
			toast.error('Select at least one token category');
			return;
		}
		const trimmedPrompt = customPrompt.trim();
		if (preset === 'custom' && !trimmedPrompt) {
			toast.error('Enter a prompt');
			return;
		}
		setSubmitted(true);
		trackDesignSystemAction('export_ds_ai');
		closeModal();

		const filename = buildExportFilename(activeDesignSystem.title, preset, 'design-system', 'md');
		await runExportWithToast({
			pendingLabel:
				preset === 'custom'
					? 'Writing your .md — this may take a minute or two…'
					: 'Preparing your .md…',
			run: async () => {
				const { exportId } = await forge.exportDsAi({
					designSystemId: activeDesignSystem.id,
					preset,
					componentNames: preset === 'components' ? Array.from(selectedComponents) : undefined,
					tokenDomains: preset === 'tokens' ? Array.from(selectedDomains) : undefined,
					customPrompt: preset === 'custom' ? trimmedPrompt : undefined,
					filename,
				});
				return { exportId, filename };
			},
		}).catch((e) => Sentry.captureException(e));
	};

	//----------------------------------------------------------------------
	// RENDERING
	//----------------------------------------------------------------------

	const domainLabel = (key: string): string =>
		(CATEGORY_LABEL_MAP as Record<string, string>)[key] ?? titleCase(key);

	const submitDisabled =
		submitted ||
		!activeDesignSystem ||
		(preset === 'components' && selectedComponents.size === 0) ||
		(preset === 'tokens' && selectedDomains.size === 0) ||
		(preset === 'custom' && !customPrompt.trim());

	const renderScope = () => {
		if (preset === 'all') return null;
		if (preset === 'custom') {
			return (
				<Shared.OptionsGroup>
					<S.PromptTextarea
						aria-label="Custom prompt"
						value={customPrompt}
						onChange={(e) => setCustomPrompt(e.target.value)}
						placeholder="Describe what you want — e.g. a one-page cheat sheet of primary button styles and our three most important color tokens"
						rows={6}
					/>
				</Shared.OptionsGroup>
			);
		}
		if (preset === 'components') {
			if (allComponents.length === 0) {
				return (
					<Shared.OptionsGroup>
						<S.EmptyNote>No components in this design system.</S.EmptyNote>
					</Shared.OptionsGroup>
				);
			}
			return (
				<Shared.OptionsGroup>
					<SelectAllRow
						selectedCount={selectedComponents.size}
						totalCount={allComponents.length}
						onChange={(mode) =>
							setSelectedComponents(
								mode === 'all' ? new Set(allComponents.map((c) => c.dir)) : new Set()
							)
						}
					/>
					<S.ScopeList>
						{allComponents.map((c) => {
							const checked = selectedComponents.has(c.dir);
							return (
								<S.ScopeRow key={c.dir} $isSelected={checked}>
									<S.ScopeRowInput
										type="checkbox"
										checked={checked}
										onChange={() => toggleComponent(c.dir)}
									/>
									{c.label}
								</S.ScopeRow>
							);
						})}
					</S.ScopeList>
				</Shared.OptionsGroup>
			);
		}
		// tokens
		if (allDomains.length === 0) {
			return (
				<Shared.OptionsGroup>
					<S.EmptyNote>No token categories available.</S.EmptyNote>
				</Shared.OptionsGroup>
			);
		}
		return (
			<Shared.OptionsGroup>
				<SelectAllRow
					selectedCount={selectedDomains.size}
					totalCount={allDomains.length}
					onChange={(mode) =>
						setSelectedDomains(mode === 'all' ? new Set(allDomains) : new Set())
					}
				/>
				<S.ScopeList>
					{allDomains.map((d) => {
						const checked = selectedDomains.has(d);
						return (
							<S.ScopeRow key={d} $isSelected={checked}>
								<S.ScopeRowInput
									type="checkbox"
									checked={checked}
									onChange={() => toggleDomain(d)}
								/>
								{domainLabel(d)}
							</S.ScopeRow>
						);
					})}
				</S.ScopeList>
			</Shared.OptionsGroup>
		);
	};

	return (
		<Modal
			title="Export for AI"
			description="Get .MD files for Claude Code, ChatGPT, Codex, Cursor, etc."
			onClose={closeModal}
			onCancel={closeModal}
			onSubmit={onGenerate}
			submitLabel="Download"
			disabled={submitDisabled}
			loading={false}
		>
			<Shared.Body>
				<S.PresetGrid>
					{PRESET_CARDS.map((card) => (
						<RadioCard
							key={card.preset}
							active={preset === card.preset}
							label={card.label}
							description={card.description}
							onClick={() => setPreset(card.preset)}
						/>
					))}
				</S.PresetGrid>
				{renderScope()}
			</Shared.Body>
		</Modal>
	);
};

export default DsAiModal;
